import { AxiosInstance } from 'axios';

import { TripRepositoryMock } from '@src/mocks/repositories/trip.repository.mock';
import { Trip } from '@src/types/api/models';
import { translatedChannelCode } from '@src/utils/trip';

import { TripRepository } from './trip.repository';
import { TripRepositoryAxios } from './trip.repository.axios';

type PaginationProps = {
  offset: number;
  limit: number;
};

export class TripService {
  private constructor(private readonly tripRepository: TripRepository) {}

  static getChannelName(trip: Trip) {
    return translatedChannelCode[trip.channelCode] ?? trip.channelCode;
  }

  async getAllTrips(locale: string) {
    return this.tripRepository.getAllTrips({ locale });
  }

  async getUpcomingTrips(locale: string) {
    const { data } = await this.tripRepository.getTrips({
      locale,
      status: ['Confirmed', 'Pending', 'InHouse'],
      orderBy: 'arrival:asc',
    });
    return data;
  }

  async getPastCanceledTrips(locale: string, { offset, limit }: PaginationProps) {
    return this.tripRepository.getTrips({
      locale,
      status: ['CheckedOut', 'Canceled'],
      offset,
      limit,
      orderBy: 'departure:desc',
    });
  }

  async getCurrentTrip(locale: string) {
    return this.tripRepository.getCurrentTrip({ locale });
  }

  async getTripById(tripId: string, locale: string) {
    return this.tripRepository.getTripById({ tripId, locale });
  }

  async getLinkedTrips(reservationId: string, locale: string) {
    return this.tripRepository.getLinkedTrips({ reservationId, locale });
  }

  async linkTripToGuest({ reservationIdOrCode, lastName }: { reservationIdOrCode: string; lastName: string }) {
    return this.tripRepository.linkTripToGuest({ reservationIdOrCode, lastName });
  }

  public static create(axiosInstance: AxiosInstance): TripService {
    const isMockEnabled = process.env.NEXT_PUBLIC_MOCKS_ACTIVE === 'true';

    const tripRepository = isMockEnabled
      ? new TripRepositoryMock(axiosInstance)
      : new TripRepositoryAxios(axiosInstance);

    return new TripService(tripRepository);
  }
}
